import { createContext, useContext, useState, useCallback, forwardRef, type ReactNode } from "react";
import { translations } from "@/i18n/translations";

type Language = "en" | "bn";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = forwardRef<HTMLDivElement, { children: ReactNode }>(({ children }, _ref) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem("preferred_language");
      if (saved === "bn" || saved === "en") return saved;
    } catch {}
    return "en";
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem("preferred_language", lang); } catch {}
  }, []);

  const t = useCallback((key: string, fallback?: string): string => {
    const dict = translations[language];
    if (dict && key in dict) return dict[key as keyof typeof dict];
    // fallback to English
    const enDict = translations.en;
    if (enDict && key in enDict) return enDict[key as keyof typeof enDict];
    return fallback || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
});

LanguageProvider.displayName = "LanguageProvider";

const fallbackContext: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: (key: string, fallback?: string) => fallback || key,
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) return fallbackContext;
  return ctx;
};
